import type { MenuItem } from "primevue/menuitem";

import { defineStore } from "pinia";
import { type Ref, readonly, ref, watch } from "vue";
import { type ELocale, INITIAL_LOCALE } from "~application/constants/locale.constant.ts";

export const APPLICATION_STORE_KEY = "APPLICATION_STORE" as const;

export type TSearchType = "site" | "pilot" | "modulator";

export type UseApplicationStoreReturn = {
  isSidebarCollapsed: Ref<boolean>;
  locale: Ref<ELocale>;
  isDarkMode: Ref<boolean>;
  submenuItems: ReturnType<typeof readonly<Ref<MenuItem[]>>>;
  searchHistory: Ref<Record<TSearchType, string[]>>;
  setSubmenuItems(next: [MenuItem, ...MenuItem[]]): void;
  clearSubmenuItems(): void;
  addSearchHistory(type: TSearchType, value: string): void;
};

export const useApplicationStore = defineStore<typeof APPLICATION_STORE_KEY, UseApplicationStoreReturn>(
  APPLICATION_STORE_KEY,
  () => {
    const isSidebarCollapsed = ref(false);
    const locale = ref<ELocale>(INITIAL_LOCALE);
    const isDarkMode = ref<boolean>(false);
    const submenuItems = ref<MenuItem[]>([]);
    const searchHistory = ref<Record<TSearchType, string[]>>({
      site: [],
      pilot: [],
      modulator: [],
    });

    function setSubmenuItems(next: [MenuItem, ...MenuItem[]]) {
      submenuItems.value = next;
    }

    function clearSubmenuItems() {
      submenuItems.value = [];
    }

    function addSearchHistory(type: TSearchType, value: string) {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return;
      }
      const history = searchHistory.value[type];
      const newHistory = [trimmedValue, ...history.filter((item) => item !== trimmedValue)];
      searchHistory.value[type] = newHistory.slice(0, 5);
    }

    watch(
      isDarkMode,
      (isDarkMode) => {
        const theme = isDarkMode ? "dark" : "light";
        document.documentElement.classList.remove("dark", "light");
        document.documentElement.classList.add(theme);
      },
      { immediate: true },
    );

    return {
      isSidebarCollapsed,
      locale,
      isDarkMode,
      submenuItems: readonly(submenuItems),
      searchHistory,
      setSubmenuItems,
      clearSubmenuItems,
      addSearchHistory,
    } satisfies UseApplicationStoreReturn;
  },
  {
    persist: {
      pick: ["isSidebarCollapsed", "locale", "isDarkMode", "searchHistory"],
    },
  },
);
